/**
 * A named import rather than the `Popper` global.
 *
 * `createPopper` is the only thing this file used off it, and naming it is what lets
 * the bundler drop the rest of the library — something `Popper.createPopper` on a
 * global object can never do, because a property read off `window` is opaque to a
 * bundler.
 */
import { createPopper } from '@popperjs/core';

/**
 * One definition instead of four.
 *
 * An identical `delay()` was declared as a member of `cardShareContent`,
 * `cardShareButton` and `postShareButton` here, and once more in block.js.
 * See docs/media-flow-review.md S10.
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Copy text, and report whether it actually worked.
 *
 * `navigator.clipboard.writeText()` returns a promise that rejects in a non-secure
 * context, when the document is not focused, and when permission is denied. Neither
 * call site awaited it or caught, so a failed copy produced an unhandled rejection in
 * the console *and* still showed the reader "已複製到剪貼簿！".
 * See docs/media-flow-review.md S8.
 *
 * @param {string} text
 * @returns {Promise<boolean>}
 */
const copyText = async (text) => {
	if (!text) return false;

	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch (error) {
		console.warn('Copy to clipboard failed:', error);
		return false;
	}
};

document.addEventListener('alpine:init', () => {
	Alpine.data('cardShareButton', () => ({
		/**
		 * Read from the component's own `data-url` rather than a shared store.
		 *
		 * The URL used to be written to `$store.cardShare.url`, and only inside
		 * showTooltip(). Since hideTooltip() never reset `isShown`, the second click
		 * anywhere on the page took the hide branch and every click after it did too —
		 * so the store kept the *first* card's permalink for the rest of the session
		 * and the copy button shared the wrong link. Each card now owns its own URL and
		 * its own popup, which removes the failure mode rather than patching it.
		 * See docs/media-flow-review.md S2.
		 */
		url: '',
		isShown: false,
		isJustCopied: false,
		popper: null,

		init: function () {
			this.url = this.$root.dataset.url ?? '';
		},

		showTooltip: function () {
			this.isShown = true;

			this.$nextTick(() => {
				this.popper?.destroy();

				this.popper = createPopper(
					this.$refs.button,
					this.$refs.cardShareContent,
					{
						placement: 'top-end',
						// Cards live inside Swiper containers, which are
						// `overflow: hidden`; an absolutely positioned popup gets
						// clipped at the slide edge.
						strategy: 'fixed',
						modifiers: [
							{
								name: 'flip',
								options: {
									flipVariations: false,
									allowedAutoPlacements: ['top', 'end'],
								},
							},
							{
								name: 'offset',
								options: {
									offset: [0, 8],
								},
							},
						],
					}
				);
			});
		},

		hideTooltip: async function () {
			if (!this.isShown) return;

			if (this.isJustCopied) {
				await delay(2000);
			}

			// Was missing, which is what turned toggleTooltip() into a one-way switch.
			this.isShown = false;

			this.popper?.destroy();
			this.popper = null;
		},

		toggleTooltip: function () {
			if (this.isShown) {
				this.hideTooltip();
			} else {
				this.showTooltip();
			}
		},

		copyToClipboard: async function () {
			if (!(await copyText(this.url))) return;

			this.isJustCopied = true;
			await delay(2000);
			this.isJustCopied = false;
		},

		destroy: function () {
			this.popper?.destroy();
			this.popper = null;
		},
	}));

	Alpine.data('postShareButton', () => ({
		isJustCopied: false,
		isOpen: false,
		copyToClipboard: async function () {
			if (!(await copyText(this.$root.dataset.url ?? ''))) return;

			this.isJustCopied = true;
			await delay(2000);
			this.isJustCopied = false;
		},
	}));
});
